import { get } from 'svelte/store';

import { v4 as uuidv4 } from 'uuid';
import Konva from 'konva';

import determineProductColourImageUrl from '../../../utils/determineProductColourImageUrl';

import { designState, activeElement } from '../../../stores/Designer.js';
import { productDataStore } from '../../../stores/ProductData.js';
import { teamDetails } from '../../../stores/TeamDetails.js';

import { environment } from '../../../environment/environment.js';

const printPricing = environment.PRINT_PRICING;

/// ////////////CANVAS////////////////////

// https://stackoverflow.com/questions/13419101/is-it-possible-to-replace-a-color-in-a-base-64-encoded-image
export function changeColInUri(data, colto, width, height, callback) {
  // let dataURL

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  ctx.filter = 'blur(0.5px)';
  const image = new Image();
  image.crossOrigin = 'Anonymous';
  image.onload = function () {
    ctx.drawImage(image, 0, 0);
    // do actual color replacement
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    const rgbToArray = colto.replace('(', '').replace(')', '').split(',');
    const rgbto = {
      r: parseInt(rgbToArray[0]),
      g: parseInt(rgbToArray[1]),
      b: parseInt(rgbToArray[2]),
    };

    for (let x = 0, len = data.length; x < len; x += 4) {
      // Recolour the rgb channels
      // Transparent will remain unchanged
      data[x] = rgbto.r;
      data[x + 1] = rgbto.g;
      data[x + 2] = rgbto.b;
    }

    ctx.putImageData(imageData, 0, 0);

    const dataUri = canvas.toDataURL();
    callback(dataUri);
  };
  image.src = data;
}

function hexToRGB(hexStr) {
  const col = {};
  col.r = parseInt(hexStr.substr(1, 2), 16);
  col.g = parseInt(hexStr.substr(3, 2), 16);
  col.b = parseInt(hexStr.substr(5, 2), 16);
  return col;
}

/// /////////////KONVA///////////////////

export function addBackgroundImageToLayer(konvaStage, imageSrc, x, y) {
  // We will scale the 256px image up to 512px here
  // NO - Use 512px otherwise looks bad
  const layer = konvaStage.findOne('.img-layer');
  // First clear any existing image
  const existingImage = layer.findOne('.background-image');
  if (existingImage) {
    existingImage.destroy();
  }
  const img = new Konva.Image({
    name: 'background-image',
    x,
    y,
    scaleX: 1,
    scaleY: 1,
  });
  const imageObj = new Image();
  imageObj.src = imageSrc;
  imageObj.onload = function () {
    img.image(imageObj);
    layer.add(img);
    layer.draw();
  };

  return imageObj;
}

export function updateElementPosition(node, stageId, side, productType) {
  const itemPosition = {
    id: node.id(),
    x: node.x(),
    y: node.y(),
    width: node.width(),
    height: node.height(),
    rotation: node.rotation(),
    // width: node.width(),
    // height: node.height(),
    scaleX: node.scaleX(),
    scaleY: node.scaleY(),
    zIndex: node.zIndex(),
  };
  // designerState.setElementPosition(node.id(), itemPosition);
  designState.updateDesignElementPosition(stageId, itemPosition.id, productType, side, itemPosition);
}

// Update the preview image in design state in response to change of design elemet - e.g text move
export function saveDesignPreviewDesignElement(konvaStage, side, productType) {
  const dataURL = konvaStage.toDataURL({
    pixelRatio: 0.75, // reduce size to save space in localstorage
  });
  // let dataURL = "need to fix this once running on proper domain"
  designState.updateDesignPreview(konvaStage.id(), productType, side, dataURL);
}

// Update the preview image in design state in response to change of product detail - e.g. colour or type
// Needs to save both the front and back previews
export function saveDesignPreviewProductChange(konvaStage, side, productType, frontImageSrc, backImageSrc) {
  const imageLayer = konvaStage.findOne('.img-layer');
  const konvaImage = imageLayer.findOne('Image');
  const imageObj = konvaImage.image();

  // Hide all design groups
  konvaStage.find('Group').hide();

  // Show front for correct product type
  const frontGroup = konvaStage.findOne('.front-design-' + productType);
  frontGroup.show();
  konvaStage.draw();

  const dataURLFront = konvaStage.toDataURL({
    pixelRatio: 0.75, // reduce size to save space in localstorage
  });
  designState.updateDesignPreview(konvaStage.id(), productType, 'front', dataURLFront);

  // Hide front design group
  frontGroup.hide();

  // Show back
  const backGroup = konvaStage.findOne('.back-design-' + productType);
  backGroup.show();
  konvaStage.draw();

  const dataURLBack = konvaStage.toDataURL({
    pixelRatio: 0.75, // reduce size to save space in localstorage
  });
  designState.updateDesignPreview(konvaStage.id(), productType, 'back', dataURLBack);

  // Now reset stage
  backGroup.hide();

  const visibleGroup = konvaStage.findOne('.' + side + '-design-' + productType);
  visibleGroup.show();

  // Also show the Transformer again as it is hidden when all groups are hidden
  konvaStage.findOne('Transformer').show();

  konvaStage.draw();
}

export function addDesignerImageToLayer(
  konvaStage,
  imageSrc,
  side,
  groupName,
  elementProps,
  transformer,
  listening = true,
  draggable = true,
) {
  const objId = uuidv4();
  const layer = konvaStage.findOne('.design-layer');
  const group = layer.findOne('.' + groupName);

  // Initial position top left at centre
  // Reposition after onload
  const x = konvaStage.width() / 2;
  const y = konvaStage.height() / 2;

  const imageObj = new Image();
  imageObj.onload = function () {
    const img = new Konva.Image({
      id: objId,
      image: imageObj,
      x,
      y,
      scaleX: 1,
      scaleY: 1,
      listening,
      draggable,
    });
    group.add(img);

    // Calc original dimensions incase we update later
    const originalWidth = konvaStage.width() / konvaStage.scaleX();
    const originalHeight = konvaStage.height() / konvaStage.scaleY();

    // This is our default centred position
    let imageX = (originalWidth - img.width()) / 2;
    let imageY = (originalHeight - img.height()) / 2;
    let scaleX = 1;
    let scaleY = 1;

    // Can position image based on type of print and other data sent from addToKonva function elementProps.printType
    // Can position image based on type of product - elementProps.productType
    // Can position image based on side of product - elementProps.segment - contains the url like front-print or back-print

    // Position image based on product type
    switch (elementProps.productType) {
      case 'top':
        // Back names and numbers
        if (elementProps.printType === 'names & no' && elementProps.segment === 'back-print') {
          // Move higher on jersey, make larger and move to the left
          imageY -= originalHeight / 5;
          imageX -= originalWidth / 10;
          scaleX = 1.2;
          scaleY = 1.2;
        }

        // Front text
        if (elementProps.printType === 'text' && elementProps.segment === 'front-print') {
          // move a bit to the right
          imageX += originalWidth / 10;
          // Move a bit higher on jerseys and make larger
          imageY -= originalHeight / 8;
          scaleX = 1.3;
          scaleY = 1.3;
        }

        // Back text
        if (elementProps.printType === 'text' && elementProps.segment === 'back-print') {
          // Move a bit higher on jerseys and make larger
          imageY -= originalHeight / 6;
          scaleX = 1.2;
          scaleY = 1.2;
        }

        // Art front
        if (elementProps.printType === 'art' && elementProps.segment === 'front-print') {
          // Move a bit higher on jerseys
          imageY -= originalHeight / 10;
        }

        // Art back
        if (elementProps.printType === 'art' && elementProps.segment === 'back-print') {
          // Move a bit higher on jerseys
          imageY -= originalHeight / 6;
        }

        // upload front
        if (elementProps.printType === 'upload' && elementProps.segment === 'front-print') {
          // Move a bit higher on jerseys
          imageY -= originalHeight / 10;
        }

        // upload back
        if (elementProps.printType === 'upload' && elementProps.segment === 'back-print') {
          // Move a bit higher on jerseys
          imageY -= originalHeight / 6;
        }

        break;

      case 'bottom':
        // Print size and positioning for shorts and pants
        // TODO - add these
        break;
    }

    img.scaleX(scaleX);
    img.scaleY(scaleY);
    // if scale has changed then we need to update the x position to recentre
    if (scaleX !== 1) {
      imageX = (originalWidth - img.width() * scaleX) / 2;
    }

    img.x(imageX);
    img.y(imageY);

    // Remove any transformers
    transformer.nodes([]);

    // add transformer
    transformer.nodes([img]);
    layer.draw();

    // Set as active element
    activeElement.set(objId);

    const state = {
      id: objId,
      x: imageX,
      y: imageY,
      width: img.width(),
      height: img.height(),
      rotation: img.rotation(),
      scaleX,
      scaleY,
      zIndex: img.zIndex(),
      ...elementProps, // object that holds info about print - e.g. text, fontFamily, colour, etc
    };

    // If element is name number then add name number to teamDetails store
    if (elementProps.printType === 'names & no') {
      teamDetails.addDefaultTeamMember(elementProps.name, elementProps.number);
    }
    // Add element to state
    designState.addDesignElement(konvaStage.id(), elementProps.productType, side, state);

    // Change cursor for hover over shape - applied via stage.
    img.on('mouseenter', function () {
      konvaStage.container().style.cursor = 'pointer';
    });
    img.on('mouseleave', function () {
      konvaStage.container().style.cursor = 'default';
    });

    // Record dragging and transform events
    img.on('dragend', function () {
      updateElementPosition(img, konvaStage.id(), side, elementProps.productType);
      // Remove transformer and save image
      if (transformer.nodes().length > 0) {
        transformer.nodes([]);
      }
      // saveDesignPreviewDesignElement(konvaStage, side, elementProps.productType)
      transformer.nodes([img]);
    });

    img.on('transformend', function () {
      updateElementPosition(img, konvaStage.id(), side, elementProps.productType);
      if (transformer.nodes().length > 0) {
        transformer.nodes([]);
      }
      // saveDesignPreviewDesignElement(konvaStage, side, elementProps.productType)
      transformer.nodes([img]);
    });
    // TODO - add collision detection on dragend and transformed functions- make a flag in DB or order about overlapping?
    // https://konvajs.org/docs/sandbox/Collision_Detection.html

    // On click set the element ID for the edit menu?
    // mouseup touchend
    img.on('click tap mouseup touchend dragend transformend', function () {
      activeElement.set(objId);
    });
  };
  imageObj.src = imageSrc;
}

export function updateDesignerImage(konvaStage, existingElement, imageSrc, side, elementProps, transformer) {
  const layer = konvaStage.findOne('.design-layer');
  const element = layer.findOne('#' + existingElement.id);
  if (!element) {
    return;
  }
  const initialWidth = element.width();
  const imageObj = new Image();
  imageObj.onload = function () {
    element.image(imageObj);
    // Reposition to maintain centre in same spot as previous image
    if (elementProps.printType === 'names & no') {
      // Need to recenter name an number elements as their width is variable
      // all other elements have fixed width of 150px from API
      const newX = element.x() - (element.width() - initialWidth) / 2;
      element.x(newX);
    }

    // Remove any transformers
    transformer.nodes([]);
    // Update preview image in state
    // Do here before transformer is drawn otherwise it is shown as well
    // saveDesignPreviewDesignElement(konvaStage, side, elementProps.productType)

    // add transformer
    transformer.nodes([element]);
    layer.draw();

    // If element is name number then add name number to teamDetails store
    if (elementProps.printType === 'names & no') {
      teamDetails.addDefaultTeamMember(elementProps.name, elementProps.number);
    }

    // Add element to state
    designState.updateDesignElement(konvaStage.id(), existingElement.id, side, elementProps);
  };
  imageObj.src = imageSrc;
}

export function deleteElement(elementId, productType, side, konvaStage) {
  const element = konvaStage.findOne('#' + elementId);
  if (!element) {
    return;
  }
  const layer = konvaStage.findOne('.design-layer');
  const transformer = layer.find('Transformer');
  transformer.nodes([]); // reset
  element.destroy();
  designState.deleteDesignElement(konvaStage.id(), productType, side, elementId);
  // saveDesignPreviewDesignElement(konvaStage, side, productType)
  layer.draw();
}

export function moveElementUp(elementId, productType, side, konvaStage) {
  const element = konvaStage.findOne('#' + elementId);
  if (!element) {
    return;
  }
  const initialIndex = element.zIndex();
  const layer = konvaStage.findOne('.design-layer');
  element.moveUp();
  const transformer = layer.find('Transformer');
  transformer.moveToTop();
  const updatedIndex = element.zIndex();
  layer.draw();
  designState.updateElementOrder(konvaStage.id(), productType, side, elementId, initialIndex, updatedIndex);
  // saveDesignPreviewDesignElement(konvaStage, side, productType)
}

export function moveElementDown(elementId, productType, side, konvaStage) {
  const element = konvaStage.findOne('#' + elementId);
  if (!element) {
    return;
  }
  const initialIndex = element.zIndex();
  const layer = konvaStage.findOne('.design-layer');
  element.moveDown();
  const transformer = layer.find('Transformer');
  transformer.moveToTop();
  const updatedIndex = element.zIndex();
  layer.draw();
  designState.updateElementOrder(konvaStage.id(), productType, side, elementId, initialIndex, updatedIndex);
  // saveDesignPreviewDesignElement(konvaStage, side, productType)
}

export function removeTransformer(konvaStage) {
  const layer = konvaStage.findOne('.design-layer');
  const transformer = layer.find('Transformer');
  transformer.nodes([]);
}

function addDesignElements(konvaStage, layer, side, transformer, productType, designElementsArray) {
  for (const designEelement of designElementsArray) {
    const groupName = side + '-design-' + productType;
    const group = layer.findOne('.' + groupName);

    const imageObj = new Image();
    imageObj.onload = function () {
      const img = new Konva.Image({
        id: designEelement.id,
        image: imageObj,
        x: designEelement.x,
        y: designEelement.y,
        rotation: designEelement.rotation,
        scaleX: designEelement.scaleX,
        scaleY: designEelement.scaleY,
        zIndex: designEelement.zIndex,
        listening: true,
        draggable: true,
      });
      group.add(img);

      layer.draw();

      // Change cursor for hover over shape - applied via stage.
      img.on('mouseenter', function () {
        konvaStage.container().style.cursor = 'pointer';
      });
      img.on('mouseleave', function () {
        konvaStage.container().style.cursor = 'default';
      });

      // Record dragging and transform events
      img.on('dragend', function () {
        updateElementPosition(img, konvaStage.id(), side, productType);
        // Remove transformer and save image
        if (transformer.nodes().length > 0) {
          transformer.nodes([]);
        }
        // saveDesignPreviewDesignElement(konvaStage, side, productType)
        transformer.nodes([img]);
      });

      img.on('transformend', function () {
        updateElementPosition(img, konvaStage.id(), side, productType);
        if (transformer.nodes().length > 0) {
          transformer.nodes([]);
        }
        // saveDesignPreviewDesignElement(konvaStage, side, productType)
        transformer.nodes([img]);
      });
      // TODO - add collision detection on dragend and transformed functions- make a flag in DB or order about overlapping?
      // https://konvajs.org/docs/sandbox/Collision_Detection.html

      // On click set the element ID for the edit menu?
      // mouseup touchend
      img.on('click tap mouseup touchend dragend transformend', function () {
        activeElement.set(designEelement.id);
      });
    };
    imageObj.src = designEelement.image;
  }
}

export function loadDesignFromState(konvaStage, designElements, productType, transformer) {
  // konva stage will have the same id as the details in designState
  // konva stage will have been initialised with front/back image layers
  // So this function needs to only add the design items to each product
  const layer = konvaStage.findOne('.design-layer');

  const frontElements = designElements.front;
  const backElements = designElements.back;
  const leftSideElements = designElements.leftSide;
  const rightSideElements = designElements.rightSide;

  addDesignElements(konvaStage, layer, 'front', transformer, productType, frontElements);
  addDesignElements(konvaStage, layer, 'back', transformer, productType, backElements);
  addDesignElements(konvaStage, layer, 'rightSide', transformer, productType, rightSideElements);
  addDesignElements(konvaStage, layer, 'leftSide', transformer, productType, leftSideElements);
}

export function toggleVisibleGroups(konvaStage, groupName) {
  const groups = konvaStage.find('Group');
  groups.hide();
  const group = konvaStage.findOne('.' + groupName);
  group.show();
  // Transformer is included in group - need to show again, but remove nodes
  const transformer = konvaStage.findOne('Transformer');
  transformer.show();
  transformer.nodes([]);
  konvaStage.draw();
}

/// ///////////// Miscellaneous ////////////////////
export async function determineImageUrl({
  side,
  showModel,
  size,
  fileType,
  type = null,
  newColour = null,
  newSublimationTemplate = null,
  isModelBasedImages = true,
}) {
  // newColour can be set to force an alternative colour than what is saved in product data store.
  // This is used for the previous designs summary page - to show the new product option with the new colour
  let imageSrc;
  const productData = get(productDataStore);
  if (type === null || !productData?.[type]?.gender) {
    type = productData.showing;
  }

  const gender = productData?.[type].gender;
  let colour = productData?.[type].selectedColour;
  let sublimationTemplate = productData?.[type].selectedSublimationTemplate;

  if (newColour) {
    colour = newColour;
  }

  if (newSublimationTemplate) {
    sublimationTemplate = newSublimationTemplate;
  }

  // Sometimes can get here without having a selectedColour set?
  if (!colour) {
    colour = productData[type].colours[0];
    // Set selected colour in product data store as well
    productData[type].selectedColour = colour;
    productDataStore.set(productData);
  }

  // Sometimes can get here without having a selectedSublimationTemplate set?
  if (!sublimationTemplate) {
    sublimationTemplate = productData[type].sublimationTemplates[0];
    // Set selected sublimation template in product data store as well
    productData[type].selectedSublimationTemplate = sublimationTemplate;
    productDataStore.set(productData);
  }

  imageSrc = await determineProductColourImageUrl({
    productSlug: productData[type].rootSlug,
    colourPathName: colour.pathName,
    colourObject: colour,
    imageSize: size,
    imageType: fileType,
    gender,
    availableGenders: [colour.mens ? 'mens' : '', colour.womens ? 'womens' : '', colour.youth ? 'youth' : ''],
    side,
    showModel,
    isModelBasedImages,
    sublimationTemplateId: sublimationTemplate?.SublimationTemplate?.id,
  });

  return imageSrc;
}

export function designElementSummary(elementArray) {
  let textCount = 0;
  let artCount = 0;
  let uploadCount = 0;
  let namenumberCount = 0;

  let includesEmbroidery = false;
  let price = 0;

  if (elementArray.length) {
    elementArray.map(function (element) {
      if (!element?.printType) {
        return;
      }
      if (element.printOption === 'embroidery') {
        includesEmbroidery = true;
      }
      switch (element.printType) {
        case 'text':
          price += printPricing.text;
          textCount += 1;
          break;
        case 'art':
          price += printPricing.art;
          artCount += 1;
          break;
        case 'upload':
          price += printPricing.upload;
          uploadCount += 1;
          break;
        case 'names & no':
          price += printPricing.namenumber;
          namenumberCount = +1;
          break;
      }
      // Set a max price
      if (price > printPricing.maxPricePerSide) {
        price = printPricing.maxPricePerSide;
      }
    });
  }

  const summary = [];

  if (textCount > 0) {
    summary.push(textCount + ' x Text');
  }

  if (artCount > 0) {
    summary.push(artCount + ' x Art');
  }

  if (uploadCount > 0) {
    summary.push(uploadCount + ' x Upload');
  }

  if (namenumberCount > 0) {
    summary.push(namenumberCount + ' x Name & No');
  }

  const summaryText = `${summary.join(', ')}${includesEmbroidery ? ' (includes embroidery)' : ''}`;

  if (price > 0) {
    return {
      summary: summaryText,
      price,
    };
  } else {
    return null;
  }
}

export function create_clipping_function(clip_func_json) {
  // clip_func_json is one option from below clipPathCommands
  // e.g. clipPathCommands.heatTransfer.singlet.mens.front
  const clipping_function = function clip_func(ctx) {
    let i = 0;
    for (i = 0; i < clip_func_json.length; ++i) {
      ctx[clip_func_json[i].op].apply(ctx, clip_func_json[i].args);
    }
  };
  return clipping_function;
}

export function updateProductClipPath(konvaStage, productData) {
  const side = productData.side;
  const productType = productData.showing;
  const gender = productData[productType]?.gender;
  const printPositions = productData[productType].printPositions;

  if (konvaStage) {
    // We need the stage to exist
    const konvaGroup = konvaStage.findOne(`.${side}-design-${productType}`);
    if (konvaGroup) {
      // clip paths are deprecated in favour of the new product images and print positions
      if (printPositions) {
        // Apply the bounding box as a konva simple clip
        const boundingBox = printPositions[side][gender].boundingBox;

        konvaGroup.clip({
          x: boundingBox.left,
          y: boundingBox.top,
          width: boundingBox.width,
          height: boundingBox.height,
        });
      } else if (productData[productType]?.clipPath) {
        const clipPathData = JSON.parse(productData[productType].clipPath);
        const clipPathCommands = clipPathData.heatTransfer[gender][side];
        // Set new clipping function
        konvaGroup.clipFunc(create_clipping_function(clipPathCommands));
      } else {
        // Use default clip function
        konvaGroup.clip({
          x: 120,
          y: 70,
          width: 255,
          height: 475,
        });
      }
    }
  }
}
